import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, InfoOutlined as InfoOutlinedIcon, Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../api';
import { extractApiErrorMessage } from '../apiErrors';
import { useCurrentUser } from '../auth';
import PageBreadcrumbs from '../components/Breadcrumbs';
import {
  analyzeStatementXmlSegments,
  StatementXmlEditor,
  serializeStatementXmlSegments,
  statementXmlToPlainText,
  type StatementSegment,
} from '../components/statement-nodes';
import { getLanguageDisplayName } from '../components/product-details';
import { TRANSLATION_TRANSLATE_PERMISSION } from '../constants/permissions';

type AutoTranslationStatus = 'not_requested' | 'skipped_non_base' | 'completed' | 'partial_failure' | 'failed';

interface AutoTranslationFailure {
  language: string;
  code: string;
  detail: string;
}

interface AutoTranslationResult {
  status: AutoTranslationStatus;
  attempted_languages: string[];
  succeeded_languages: string[];
  failed_languages: AutoTranslationFailure[];
}

interface TranslationSaveResponse {
  auto_translation?: AutoTranslationResult;
  auto_regenerate_translations_available?: boolean;
}

interface TranslationLatestResponse {
  text: string;
  auto_regenerate_translations_available?: boolean;
}

interface SaveFeedback {
  severity: 'success' | 'info' | 'warning';
  message: string;
}

const TranslationEdit: React.FC = () => {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const { permissions } = useCurrentUser();
  const { id: productId = '', language = '' } = useParams();
  const [searchParams] = useSearchParams();
  const shouldAutoProcess = searchParams.get('autoProcess') === '1';
  const fromCreate = searchParams.get('fromCreate') === '1';
  const shouldSimulateProcessing = shouldAutoProcess && fromCreate;

  const [productName, setProductName] = useState('');
  const [productBaseLanguage, setProductBaseLanguage] = useState('');
  const [text, setText] = useState('');
  const [xmlSegments, setXmlSegments] = useState<StatementSegment[] | null>(null);
  const [baseReferenceSegments, setBaseReferenceSegments] = useState<StatementSegment[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [autoProcessState, setAutoProcessState] = useState<'idle' | 'processing' | 'success' | 'failed'>('idle');
  const [clpStructureOutdated, setClpStructureOutdated] = useState(false);
  const [regenerateExistingTranslations, setRegenerateExistingTranslations] = useState(true);
  const [autoRegenerateTranslationsAvailable, setAutoRegenerateTranslationsAvailable] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState<SaveFeedback | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!productId || !language) return;

    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const load = async () => {
      const autoProcessMinDurationMs = shouldSimulateProcessing ? 5000 + Math.floor(Math.random() * 5001) : 0;
      const startedAt = Date.now();
      setLoading(true);
      setError(null);
      setSaveFeedback(null);
      setAutoProcessState(shouldAutoProcess ? 'processing' : 'idle');
      try {
        const [productData, translationData] = await Promise.all([
          api.get(`/products/${productId}`),
          api.get(`/products/${productId}/translations/${language}`),
        ]);

        const translationResponse = (translationData || {}) as TranslationLatestResponse;
        let loadedText = translationResponse.text || '';
        setProductName(productData?.name || '');
        const baseLanguage = (productData?.base_language || '').toLowerCase();
        setProductBaseLanguage(baseLanguage);
        const nextAutoRegenerateAvailable = Boolean(translationResponse.auto_regenerate_translations_available);
        setAutoRegenerateTranslationsAvailable(nextAutoRegenerateAvailable);
        setRegenerateExistingTranslations(nextAutoRegenerateAvailable);

        let parsed = analyzeStatementXmlSegments(loadedText);
        let segments = parsed.segments;
        let hasNodes = parsed.hasNodes;
        let hasMissingGlossaryFields = parsed.hasMissingGlossaryFields;

        if (shouldAutoProcess) {
          if (!hasNodes && loadedText.trim().length > 0) {
            try {
              const conversionResponse = await api.post('/convert-to-xml', { text: loadedText, language });
              const convertedText = (conversionResponse?.converted_text || conversionResponse?.convertedText) as
                | string
                | undefined;

              if (convertedText) {
                await api.put(`/products/${productId}/translations/${language}`, { text: convertedText });
                loadedText = convertedText;
                parsed = analyzeStatementXmlSegments(convertedText);
                segments = parsed.segments;
                hasNodes = parsed.hasNodes;
                hasMissingGlossaryFields = parsed.hasMissingGlossaryFields;
              }
            } catch {
              // Keep fallback behavior for manual conversion and show warning state.
            }
          }

          setAutoProcessState(hasNodes ? 'success' : 'failed');
        }

        setText(hasMissingGlossaryFields ? parsed.plainText : loadedText);
        setXmlSegments(hasNodes ? segments : null);
        setClpStructureOutdated(hasMissingGlossaryFields);

        if (baseLanguage && baseLanguage !== language.toLowerCase()) {
          try {
            const baseTranslation = await api.get(`/products/${productId}/translations/${baseLanguage}`);
            const parsedBase = analyzeStatementXmlSegments(baseTranslation?.text || '');
            setBaseReferenceSegments(parsedBase.hasNodes ? parsedBase.segments : null);
          } catch {
            setBaseReferenceSegments(null);
          }
        } else {
          setBaseReferenceSegments(null);
        }
      } catch {
        setError(t('errors.fetchFailed'));
        setProductName('');
        setProductBaseLanguage('');
        setText('');
        setXmlSegments(null);
        setClpStructureOutdated(false);
        setBaseReferenceSegments(null);
        setAutoRegenerateTranslationsAvailable(false);
        setRegenerateExistingTranslations(false);
      } finally {
        if (shouldSimulateProcessing && autoProcessMinDurationMs > 0) {
          const elapsedMs = Date.now() - startedAt;
          const remainingMs = autoProcessMinDurationMs - elapsedMs;
          if (remainingMs > 0) {
            await sleep(remainingMs);
          }
        }
        setLoading(false);
      }
    };

    load();
  }, [api, language, productId, shouldAutoProcess, shouldSimulateProcessing, t]);

  const isXmlText = !!xmlSegments;
  const isBaseLanguageEdit = productBaseLanguage.length > 0 && productBaseLanguage === language.toLowerCase();
  const canRegenerateExistingTranslations =
    isBaseLanguageEdit && permissions.includes(TRANSLATION_TRANSLATE_PERMISSION);
  const canUseAutoRegenerateTranslations =
    canRegenerateExistingTranslations && autoRegenerateTranslationsAvailable;

  useEffect(() => {
    if (!canUseAutoRegenerateTranslations) {
      setRegenerateExistingTranslations(false);
    }
  }, [canUseAutoRegenerateTranslations]);

  const effectiveText = useMemo(() => (xmlSegments ? serializeStatementXmlSegments(xmlSegments) : text), [xmlSegments, text]);

  const canSave = useMemo(() => {
    if (!isXmlText) return false;
    return statementXmlToPlainText(effectiveText).trim().length > 0;
  }, [effectiveText, isXmlText]);

  const handleCancel = () => {
    if (saving) return;
    navigate(`/products/${productId}`);
  };

  const handleSave = async () => {
    if (!productId || !language || !isXmlText || !canSave) return;

    setSaving(true);
    setError(null);
    setSaveFeedback(null);
    try {
      const response = (await api.put(`/products/${productId}/translations/${language}`, {
        text: effectiveText,
        regenerate_existing_translations: canUseAutoRegenerateTranslations ? regenerateExistingTranslations : false,
      })) as TranslationSaveResponse;
      const nextAutoRegenerateAvailable = Boolean(response?.auto_regenerate_translations_available);
      setAutoRegenerateTranslationsAvailable(nextAutoRegenerateAvailable);
      setRegenerateExistingTranslations(nextAutoRegenerateAvailable);

      const autoTranslation = response?.auto_translation;
      const failedLanguages = autoTranslation?.failed_languages?.map((item) => item.language.toUpperCase()).join(', ') || '';
      const succeededLanguages = autoTranslation?.succeeded_languages?.map((code) => code.toUpperCase()).join(', ') || '';

      let nextFeedback: SaveFeedback = {
        severity: 'success',
        message: t('translation.saveSuccess'),
      };

      switch (autoTranslation?.status) {
        case 'completed':
          nextFeedback =
            autoTranslation.attempted_languages.length === 0
              ? {
                  severity: 'info',
                  message: t('translation.autoRegenerateNoTargets'),
                }
              : {
                  severity: 'success',
                  message: t('translation.autoRegenerateCompleted', { languages: succeededLanguages }),
                };
          break;
        case 'partial_failure':
          nextFeedback = {
            severity: 'warning',
            message: t('translation.autoRegeneratePartialFailure', { languages: failedLanguages }),
          };
          break;
        case 'failed':
          nextFeedback = {
            severity: 'warning',
            message: t('translation.autoRegenerateFailed', { languages: failedLanguages }),
          };
          break;
        case 'not_requested':
        case 'skipped_non_base':
        default:
          nextFeedback = {
            severity: 'success',
            message: t('translation.saveSuccess'),
          };
          break;
      }

      setSaveFeedback(nextFeedback);
    } catch (err) {
      setError(extractApiErrorMessage(err) || t('errors.fetchFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleConvertToNewFormat = async () => {
    if (loading || saving || converting || !text.trim() || !language) return;

    setConverting(true);
    setError(null);
    try {
      const response = await api.post('/convert-to-xml', { text, language });
      const convertedText = (response && (response.converted_text || response.convertedText)) as string | undefined;
      if (!convertedText) {
        setError(t('statementXml.reanalyzeFailed'));
        return;
      }

      const parsed = analyzeStatementXmlSegments(convertedText);
      setText(parsed.hasMissingGlossaryFields ? parsed.plainText : convertedText);
      setXmlSegments(parsed.hasNodes ? parsed.segments : null);
      setClpStructureOutdated(parsed.hasMissingGlossaryFields);
      const hasNodes = parsed.hasNodes;
      if (!hasNodes) setError(t('statementXml.reanalyzeFailed'));
    } catch {
      setError(t('statementXml.reanalyzeFailed'));
    } finally {
      setConverting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <PageBreadcrumbs
        items={[
          { label: t('nav.products'), path: '/products' },
          { label: productName || productId, path: `/products/${productId}` },
          { label: t('translation.editPageTitle') },
        ]}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            {t('translation.editPageTitle')}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar sx={{ width: 22, height: 22, bgcolor: 'secondary.main', fontSize: '0.75rem' }}>{language.toUpperCase()}</Avatar>
            <Typography variant="body2" color="text.secondary">
              {getLanguageDisplayName(language)}
            </Typography>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          {canRegenerateExistingTranslations && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={regenerateExistingTranslations}
                  onChange={(event) => setRegenerateExistingTranslations(event.target.checked)}
                  disabled={loading || saving || !canUseAutoRegenerateTranslations}
                />
              }
              label={
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body2">
                    {t('translation.autoRegenerateToggleLabel')}
                  </Typography>
                  <Tooltip
                    title={
                      canUseAutoRegenerateTranslations
                        ? t('translation.autoRegenerateToggleHelp')
                        : t('translation.autoRegenerateToggleUnavailable')
                    }
                    arrow
                  >
                    <InfoOutlinedIcon
                      color={canUseAutoRegenerateTranslations ? 'action' : 'warning'}
                      sx={{ cursor: 'help', fontSize: '0.95rem' }}
                    />
                  </Tooltip>
                </Stack>
              }
              sx={{ mr: 1 }}
            />
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={handleCancel}
            disabled={saving}
            startIcon={<ArrowBackIcon />}
          >
            {t('translation.back')}
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={loading || saving || !canSave}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {saving ? t('translation.saving') : t('translation.save')}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {saveFeedback && (
        <Alert severity={saveFeedback.severity} sx={{ mb: 2 }}>
          {saveFeedback.message}
        </Alert>
      )}

      {fromCreate && autoProcessState === 'processing' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('translation.sourceApprovalPreparing')}
        </Alert>
      )}

      {fromCreate && autoProcessState === 'success' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('translation.sourceApprovalReady')}
        </Alert>
      )}

      {fromCreate && autoProcessState === 'failed' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('translation.sourceApprovalFailed')}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="280px">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={24} />
            <Typography>{fromCreate ? t('translation.sourceApprovalPreparing') : t('translation.loading')}</Typography>
          </Stack>
        </Box>
      ) : xmlSegments ? (
        <StatementXmlEditor
          segments={xmlSegments}
          language={language}
          onSegmentsChange={setXmlSegments}
          disabled={saving}
          disableClpAssignment={!isBaseLanguageEdit}
          referenceSegments={!isBaseLanguageEdit ? baseReferenceSegments : null}
          referenceLabel={t('statementXml.referenceSentenceLabel')}
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 1,
            p: 1.5,
          }}
        />
      ) : (
        <Box>
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      onClick={handleConvertToNewFormat}
                      disabled={loading || saving || converting || text.trim().length === 0}
                      startIcon={converting ? <CircularProgress size={16} /> : <RefreshIcon />}
                    >
                      {clpStructureOutdated ? t('statementXml.reanalyzeAction') : t('translation.convertToNewFormat')}
                    </Button>
                  }
                >
                  {clpStructureOutdated ? t('translation.saveRequiresReanalyzeStructure') : t('translation.saveRequiresConversion')}
                </Alert>

          <TextField
            label={t('translation.generated')}
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            multiline
            minRows={10}
            maxRows={16}
            variant="outlined"
            error
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                lineHeight: 1.6,
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.04),
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default TranslationEdit;
